import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Smile, Meh, Frown, Heart, Sun, Cloud, CloudRain, Zap, Moon, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MOODS = [
  { id: 5, label: 'Fantastiskt', icon: Sun, color: 'bg-yellow-100 text-yellow-600 border-yellow-200', emoji: '😊' },
  { id: 4, label: 'Bra', icon: Smile, color: 'bg-emerald-100 text-emerald-600 border-emerald-200', emoji: '🙂' },
  { id: 3, label: 'Okej', icon: Meh, color: 'bg-blue-100 text-blue-600 border-blue-200', emoji: '😐' },
  { id: 2, label: 'Nere', icon: Cloud, color: 'bg-slate-100 text-slate-600 border-slate-200', emoji: '😔' },
  { id: 1, label: 'Dåligt', icon: CloudRain, color: 'bg-purple-100 text-purple-600 border-purple-200', emoji: '😢' },
];

const ACTIVITIES = [
  { id: 'work', label: 'Jobb', emoji: '💼' },
  { id: 'exercise', label: 'Träning', emoji: '🏃' },
  { id: 'social', label: 'Umgås', emoji: '👥' },
  { id: 'rest', label: 'Vila', emoji: '😴' },
  { id: 'nature', label: 'Natur', emoji: '🌳' },
  { id: 'hobby', label: 'Hobby', emoji: '🎨' },
  { id: 'family', label: 'Familj', emoji: '👨‍👩‍👧' },
  { id: 'food', label: 'God mat', emoji: '🍽️' },
];

export default function MoodPage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [entries, setEntries] = useState([]);
  const [todayEntry, setTodayEntry] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [note, setNote] = useState('');
  const [view, setView] = useState('log'); // 'log' or 'history'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (hasAccess) {
      loadData();
    }
  }, [hasAccess]);

  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/api/mood-logs`, {
        headers: getAuthHeader()
      });
      const data = res.data || [];
      setEntries(data);
      
      const todayData = data.find(e => e.date === today);
      if (todayData) {
        setTodayEntry(todayData);
        setSelectedMood(todayData.mood);
        setSelectedActivities(todayData.activities || []);
        setNote(todayData.note || '');
      }
    } catch (err) {
      console.error('Failed to load mood data:', err);
      toast.error('Kunde inte ladda humördata');
    } finally {
      setLoading(false);
    }
  };

  const saveEntry = async () => {
    if (!selectedMood) {
      toast.error('Välj ett humör');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/api/mood-logs`, {
        date: today,
        mood: selectedMood,
        activities: selectedActivities,
        note: note
      }, {
        headers: getAuthHeader()
      });

      const entry = {
        date: today,
        mood: selectedMood,
        activities: selectedActivities,
        note: note
      };

      const newEntries = entries.filter(e => e.date !== today);
      newEntries.unshift(entry);
      setEntries(newEntries);
      setTodayEntry(entry);
      
      toast.success('Humör sparat!');
    } catch (err) {
      console.error('Failed to save mood:', err);
      toast.error('Kunde inte spara humör');
    } finally {
      setSaving(false);
    }
  };

  const toggleActivity = (activityId) => {
    setSelectedActivities(prev => 
      prev.includes(activityId) 
        ? prev.filter(a => a !== activityId)
        : [...prev, activityId]
    );
  };

  // Calculate average mood for last 7 days
  const last7Days = entries.filter(e => {
    const entryDate = new Date(e.date);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return entryDate >= weekAgo;
  });
  const avgMood = last7Days.length > 0 
    ? (last7Days.reduce((sum, e) => sum + e.mood, 0) / last7Days.length).toFixed(1)
    : null;

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-white" data-testid="mood-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full w-9 h-9 p-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Humör</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att spåra ditt humör.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="mood-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full w-9 h-9 p-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Humör</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-8 flex justify-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="mood-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full w-9 h-9 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-semibold">Humör</h1>
            <p className="text-xs text-muted-foreground">
              {todayEntry ? 'Dagens humör loggat' : 'Hur mår du idag?'}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Tabs */}
        <div className="flex gap-2 justify-center">
          <Button 
            variant={view === 'log' ? 'default' : 'outline'}
            onClick={() => setView('log')}
            className="rounded-full"
            data-testid="mood-tab-log"
          >
            Logga
          </Button>
          <Button 
            variant={view === 'history' ? 'default' : 'outline'}
            onClick={() => setView('history')}
            className="rounded-full"
            data-testid="mood-tab-history"
          >
            Historik
          </Button>
        </div>

        {view === 'log' ? (
          <>
            {/* Mood selector */}
            <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
              <h3 className="font-semibold text-center">Hur mår du just nu?</h3>
              <div className="flex justify-center gap-3">
                {MOODS.map(mood => (
                  <button
                    key={mood.id}
                    onClick={() => setSelectedMood(mood.id)}
                    data-testid={`mood-select-${mood.id}`}
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl transition-all ${
                      selectedMood === mood.id 
                        ? `${mood.color} border-2 scale-110 shadow-lg`
                        : 'bg-secondary hover:scale-105'
                    }`}
                  >
                    {mood.emoji}
                  </button>
                ))}
              </div>
              {selectedMood && (
                <p className="text-center text-muted-foreground">
                  {MOODS.find(m => m.id === selectedMood)?.label}
                </p>
              )}
            </div>

            {/* Activities */}
            <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
              <h3 className="font-semibold">Vad har du gjort idag?</h3>
              <div className="flex flex-wrap gap-2">
                {ACTIVITIES.map(activity => (
                  <Button
                    key={activity.id}
                    variant={selectedActivities.includes(activity.id) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleActivity(activity.id)}
                    className="rounded-full"
                    data-testid={`activity-${activity.id}`}
                  >
                    {activity.emoji} {activity.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Note */}
            <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
              <h3 className="font-semibold">Anteckning (valfritt)</h3>
              <Textarea
                placeholder="Hur var din dag? Vad påverkade ditt humör?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="rounded-xl resize-none"
                rows={3}
                data-testid="mood-note"
              />
            </div>

            {/* Save button */}
            <Button 
              onClick={saveEntry} 
              className="w-full rounded-full py-6"
              disabled={saving}
              data-testid="mood-save-btn"
            >
              {saving ? 'Sparar...' : (todayEntry ? 'Uppdatera' : 'Spara')}
            </Button>
          </>
        ) : (
          <>
            {/* Stats */}
            {avgMood && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">Snitt senaste 7 dagarna</p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-3xl font-bold text-purple-600">{avgMood}</span>
                    <span className="text-2xl">{MOODS.find(m => m.id === Math.round(parseFloat(avgMood)))?.emoji}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Week chart */}
            <div className="bg-white rounded-2xl p-4 border border-border/30">
              <h3 className="font-semibold mb-4">Senaste 14 dagarna</h3>
              <div className="flex justify-between items-end gap-1 h-32">
                {Array.from({ length: 14 }).map((_, i) => {
                  const date = new Date();
                  date.setDate(date.getDate() - (13 - i));
                  const dateStr = date.toISOString().split('T')[0];
                  const entry = entries.find(e => e.date === dateStr);
                  const moodValue = entry?.mood || 0;
                  const height = moodValue ? (moodValue / 5) * 100 : 10;
                  const mood = MOODS.find(m => m.id === moodValue);
                  
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div 
                        className={`w-full rounded-t-lg transition-all ${mood ? mood.color.split(' ')[0] : 'bg-slate-100'}`}
                        style={{ height: `${height}%`, minHeight: '8px' }}
                        title={entry ? `${mood?.label}: ${entry.note || ''}` : 'Ingen data'}
                      />
                      <span className="text-[10px] text-muted-foreground">
                        {date.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* History list */}
            <div className="space-y-2">
              <h3 className="font-semibold">Alla loggningar</h3>
              {entries.length > 0 ? (
                entries.slice(0, 30).map(entry => {
                  const mood = MOODS.find(m => m.id === entry.mood);
                  const activities = ACTIVITIES.filter(a => entry.activities?.includes(a.id));
                  
                  return (
                    <div key={entry.date} className="bg-white rounded-xl p-4 border border-border/30">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{mood?.emoji}</span>
                        <div className="flex-1">
                          <p className="font-medium">{mood?.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(entry.date).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </p>
                        </div>
                      </div>
                      {activities.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {activities.map(a => (
                            <span key={a.id} className="text-xs bg-secondary px-2 py-1 rounded-full">
                              {a.emoji} {a.label}
                            </span>
                          ))}
                        </div>
                      )}
                      {entry.note && (
                        <p className="text-sm text-muted-foreground mt-2 italic">"{entry.note}"</p>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-muted-foreground py-8">Ingen historik ännu</p>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
